<!-- @ts-nocheck -->
<template>
  <div :class="bem.b()">
    <a-spin :spinning="props.loading">
      <div v-if="innerValue.length > 0" :class="bem.e('list')">
        <key-value-item
          v-for="(_item, idx) in innerValue"
          :key="_item.guid"
          :ref="(el) => setKeyValueItems(el, _item.guid)"
          v-model:value="innerValue[idx]"
          :idx="idx"
          :key-label="props.keyLabel"
          :value-label="props.valueLabel"
          :key-value-type-dict="keyValueTypeDict"
          :key-value-type="props.keyValueType"
          :key-type="props.keyType"
          :key-types="props.keyTypes"
          :value-type="props.valueType"
          :values="innerValue"
          :disabled="props.disabled"
          :disabled-value="props.disabledValue"
          :disabled-key="props.disabledKey"
          :disabled-delete="props.disabledDelete"
          :keep-last="props.keepLast"
          @delete="handleDelete(idx)"
        >
          <template
            v-for="item in Object.keys($slots)"
            :key="item"
            #[item]="data"
          >
            <slot :name="item" v-bind="data || {}" :index="idx"></slot>
          </template>
        </key-value-item>
      </div>

      <ll-button
        :class="bem.e('add')"
        :loading="loading"
        :disabled="props.disabled || props.disabledAdd"
        @click="handleAddItem"
      >
        <div :class="bem.e('plus')">
          <ll-icon
            v-if="!loading"
            icon-name="icon-add"
            :class="bem.e('icon-add')"
          />
          {{ props.addText }}
        </div>
      </ll-button>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, computed, watch, nextTick } from 'vue'
import { Form } from 'ant-design-vue'
import { createNamespace, deepCompare, hasValue, guid } from '@ll-plus/utils'
import {
  keyValueProps,
  keyValueEmits,
  getKeyValueItem,
  type IKeyValueItem,
  type IKeyValueReturn,
  type IKeyValueAddEmits,
} from './config'
import keyValueItem from './components/key-value-item.vue'
import type { FormColumn } from '@ll-plus/components'

type KeyValueItemInstance = InstanceType<typeof keyValueItem>

defineOptions({ name: 'LlKeyValue' })

const props = defineProps(keyValueProps)
const emits = defineEmits(keyValueEmits)
const bem = createNamespace('key-value')
const formItemContext = Form.useInjectFormItemContext()

const innerValue = ref<IKeyValueItem[]>(
  !props.value || (props.value && !props.value.length)
    ? ([{ key: null, value: null, guid: guid() }] as IKeyValueItem[])
    : (props.value.map((e) => {
        return {
          ...e,
          guid: guid(),
        }
      }) as IKeyValueItem[])
)
const keyValueItems = ref<Record<string, KeyValueItemInstance>>({})

const setKeyValueItems = (
  el: KeyValueItemInstance | null,
  type: string
): void => {
  if (el) {
    keyValueItems.value[type] = el
  }
}

const keyValueTypeDict = computed<Record<string, FormColumn>>(() => {
  const obj: Record<string, FormColumn> = {}
  props.keyTypes.forEach((e) => {
    obj[e.value] = e.option
  })
  return obj
})

const validate = async (): Promise<{ valid: boolean; errors?: any[] }> => {
  await nextTick()

  const validationPromises = Object.values(keyValueItems.value).map(
    async (item) => {
      try {
        await item.validate()
        return { item, valid: true }
      } catch (error) {
        console.error('Validation failed for item:', item, error)
        return { item, valid: false, error }
      }
    }
  )

  const validationResults = await Promise.all(validationPromises)

  const allValid = validationResults.every((result) => result.valid)
  const errors = validationResults.filter((result) => !result.valid)

  if (!allValid) {
    console.log('Validation errors:', errors)
    return { valid: false, errors }
  }

  return { valid: true }
}

const resetFields = async (): Promise<void> => {
  await nextTick()
  for (const i of Object.values(keyValueItems.value)) {
    if (i) {
      i.resetFields()
    }
  }
}

const clearValidate = async (): Promise<void> => {
  await nextTick()
  for (const i of Object.values(keyValueItems.value)) {
    if (i) {
      i.clearValidate()
    }
  }
}

const handleAddItem = async (): Promise<void> => {
  const { valid } = await validate()
  if (!valid) return
  innerValue.value.push(getKeyValueItem())
  emits('add', {
    index: innerValue.value.length - 1,
    item: innerValue.value[innerValue.value.length - 1],
    value: innerValue.value,
  } as IKeyValueAddEmits)
}

const handleDelete = (idx: number): void => {
  if (innerValue.value.length === 1) {
    innerValue.value = [{ key: null, value: null, guid: guid() }]
  } else {
    innerValue.value.splice(idx, 1)
    emits('delete', idx)
  }
}

const getValue = (
  val: IKeyValueItem[] | IKeyValueReturn[]
): IKeyValueReturn[] => {
  return val
    .filter((e) => hasValue(e.key) || hasValue(e.value))
    .map((e) => ({
      key: e.key,
      value: e.value,
    }))
}

watch(
  () => props.value,
  (n: IKeyValueReturn[]) => {
    if (!n || (n && !n.length)) return
    const copyInnerValue = getValue(innerValue.value)
    const copyPropsValue = getValue(n)
    if (deepCompare(copyInnerValue, copyPropsValue)) return
    innerValue.value = [
      ...(n && n.length > 0
        ? n
        : props.keepLast
        ? [{ key: null, value: null, guid: guid() }]
        : n),
    ].map((e) => {
      return {
        ...e,
        guid: guid(),
      }
    })
  },
  {
    immediate: true,
    deep: true,
  }
)

watch(
  () => innerValue.value,
  (val: IKeyValueItem[]) => {
    const copyInnerValue = getValue(val)
    const copyPropsValue = getValue(props.value)
    if (deepCompare(copyInnerValue, copyPropsValue)) return
    try {
      formItemContext.onFieldChange()
      emits('change', copyInnerValue)
      emits('update:value', copyInnerValue)
    } catch (error) {
      console.error(error)
    }
  },
  {
    deep: true,
  }
)

defineExpose({
  validate,
  resetFields,
  clearValidate,
})
</script>
